// Area captured around the pin, in PDF page-space units (same units as pageSize) —
// not screen pixels. This is the knob for "how much of a big sheet surrounds the
// pin", not for sharpness — the exported bitmap's resolution is derived below from
// the actual source pixels available in that crop window, not from these numbers.
const DEFAULT_CROP = { width: 260, height: 195 };
// Cap the exported bitmap so a high PDF render-scale doesn't produce an oversized upload.
const MAX_SNAPSHOT_DIMENSION = 1400;

/**
 * Capture a cropped region of the rendered PDF page around a sheet point.
 * Returns a JPEG blob or null if the canvas is not available.
 */
export async function captureDrawingRegionSnapshot(
  sheetElement,
  point,
  pageSize,
  crop = DEFAULT_CROP
) {
  if (!sheetElement || !pageSize?.width || !pageSize?.height || !point) {
    return null;
  }

  const pdfCanvas = sheetElement.querySelector("canvas");
  if (!pdfCanvas) return null;

  const scaleX = pdfCanvas.width / pageSize.width;
  const scaleY = pdfCanvas.height / pageSize.height;
  const cropW = Math.min(pdfCanvas.width, crop.width * scaleX);
  const cropH = Math.min(pdfCanvas.height, crop.height * scaleY);

  let sx = point.x * scaleX - cropW / 2;
  let sy = point.y * scaleY - cropH / 2;
  sx = Math.max(0, Math.min(pdfCanvas.width - cropW, sx));
  sy = Math.max(0, Math.min(pdfCanvas.height - cropH, sy));

  // Export at (up to) the source canvas's own resolution instead of always downsampling
  // to the fixed crop.width/height — the PDF render-quality fix means the source canvas
  // is usually far higher-res than that, and resizing down to it was throwing the detail
  // away. Never upscale past the real source pixels (outputScale caps at 1).
  const outputScale = Math.min(1, MAX_SNAPSHOT_DIMENSION / Math.max(cropW, cropH, 1));
  const output = document.createElement("canvas");
  output.width = Math.max(1, Math.round(cropW * outputScale));
  output.height = Math.max(1, Math.round(cropH * outputScale));
  const ctx = output.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, output.width, output.height);
  ctx.drawImage(pdfCanvas, sx, sy, cropW, cropH, 0, 0, output.width, output.height);

  // Marker radius/stroke scale with the output so it looks the same size relative
  // to the image as it did at the old fixed 260px-wide export.
  const markerScale = output.width / crop.width;
  const pinX = ((point.x * scaleX - sx) / cropW) * output.width;
  const pinY = ((point.y * scaleY - sy) / cropH) * output.height;
  ctx.beginPath();
  ctx.arc(pinX, pinY, 7 * markerScale, 0, Math.PI * 2);
  ctx.fillStyle = "#e5484d";
  ctx.fill();
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2 * markerScale;
  ctx.stroke();

  return new Promise((resolve) => {
    output.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
  });
}
